import React, { useEffect, useState, Suspense, lazy } from 'react';
import { TrackingLink } from '@/lib/marketing';
import {
  LayoutDashboard,
  Rocket,
  ArrowRight,
  X,
  Layers
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
// Removed OptimizedImage - now using TwicPics for image optimization
import PageLayout from '@/components/askjds/PageLayout';
import { SectionDivider } from '@/components/home/SectionDivider';
import { FeatureGroup } from '@/components/home/FeatureGroup';
import { CompactFeatureRow } from '@/components/home/CompactFeatureRow';
import { useSEO, getOrganizationSchema, getWebsiteSchema, getFAQSchema } from '@/utils/seo';
import { PrelaunchBanner } from '@/components/home/PrelaunchBanner';
import { Testimonial, TRUSTPILOT_URL } from '@/components/marketing/Testimonial';
import { useIsNativeApp } from '@/components/native';

// Below-the-fold sections — lazy-loaded so the entry bundle stays small
// (2026-05-25, board 8iK8ZuVwbtHaa9Pvrtvu). HomepagePricingSection in particular
// transitively pulls Stripe code; keeping it lazy means stripe.js doesn't get
// fetched on initial homepage render.
const HomepageFlashcardDemo = lazy(() =>
  import('@/components/home/HomepageFlashcardDemo').then(m => ({ default: m.HomepageFlashcardDemo }))
);
const HomepagePricingSection = lazy(() =>
  import('@/components/home/HomepagePricingSection').then(m => ({ default: m.HomepagePricingSection }))
);
const NonServicesSection = lazy(() =>
  import('@/components/home/NonServicesSection').then(m => ({ default: m.NonServicesSection }))
);
const AboutSection = lazy(() =>
  import('@/components/home/AboutSection').then(m => ({ default: m.AboutSection }))
);

// Lightweight skeleton placeholder for the lazy sections — keeps CLS at 0
// and gives a visible "something is here" cue while the chunk loads.
const SectionPlaceholder = ({ minHeight = '320px' }: { minHeight?: string }) => (
  <div className="w-full" style={{ minHeight }} aria-hidden="true" />
);

// Chat demo images
const chatDemos = [
  {
    id: 'concept-clarification',
    image: '/images/chat/chat_demo_1.png',
    title: 'Concept Clarification',
    description: 'Example: "Explain promissory estoppel like I\'m five."',
    altText: 'Screenshot of Ask JDS explaining promissory estoppel in simple, easy-to-understand language for law students'
  },
  {
    id: 'legal-distinctions',
    image: '/images/chat/chat_demo_2.png',
    title: 'Legal Distinctions',
    description: 'Example: "What\'s the difference between negligence and strict liability?"',
    altText: 'Ask JDS chat interface showing a clear comparison between negligence and strict liability legal concepts'
  },
  {
    id: 'case-analysis',
    image: '/images/chat/chat_demo_3.png',
    title: 'Case Analysis',
    description: 'Example: "Group project gone wrong. Hadley v. Baxendale—what\'s the damage?"',
    altText: 'Screenshot demonstrating Ask JDS analyzing Hadley v. Baxendale case law with practical applications for students'
  },
  {
    id: 'rapid-summaries',
    image: '/images/chat/chat_demo_4.png',
    title: 'Rapid Topic Summaries',
    description: 'Example: "For negligence, give me a one-paragraph high-yield summary of key rules."',
    altText: 'Ask JDS providing a concise, exam-focused summary of negligence law rules in the chat interface'
  }
];

export function HomePage() {
  const { user } = useAuth();
  // Pure Client v1: no dollar prices in the native build (even in FAQ prose).
  // See docs/mobile-app/STORE-PURCHASE-STRATEGY.md.
  const isNative = useIsNativeApp();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  // SEO Configuration
  useSEO({
    title: 'Law School & Bar Prep, Simplified | JD Simplified',
    description: 'Stop guessing what to study. Practice-first law school and bar prep with case briefs, outlines, flashcards, and feedback — your progress carries forward from 1L to the bar.',
    image: '/images/og-jds-2026.png',
    url: 'https://jdsimplified.com'
  });

  // Add structured data
  useEffect(() => {
    const addStructuredData = () => {
      // Remove any existing structured data scripts
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      // Add organization schema
      const orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.textContent = JSON.stringify(getOrganizationSchema());
      document.head.appendChild(orgScript);

      // Add website schema
      const webScript = document.createElement('script');
      webScript.type = 'application/ld+json';
      webScript.textContent = JSON.stringify(getWebsiteSchema());
      document.head.appendChild(webScript);

      // Add SoftwareApplication schema for law students + bar takers
      const appScript = document.createElement('script');
      appScript.type = 'application/ld+json';
      appScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "JD Simplified",
        "url": "https://www.jdsimplified.com",
        "applicationCategory": "EducationApplication",
        "operatingSystem": "Web",
        "isAccessibleForFree": true,
        "inLanguage": "en",
        "image": "https://www.jdsimplified.com/images/og-jds-2026.png",
        "description": "Stop guessing what to study. Practice-first law school and bar prep with case briefs, outlines, flashcards, and feedback — your progress carries forward from 1L to the bar.",
        "audience": [
          { "@type": "EducationalAudience", "audienceType": "Law students" },
          { "@type": "EducationalAudience", "audienceType": "Bar takers" }
        ],
        "offers": {
          "@type": "Offer",
          "price": "0.00",
          "priceCurrency": "USD"
        },
        "publisher": {
          "@type": "Organization",
          "name": "JD Simplified",
          "url": "https://www.jdsimplified.com"
        },
        "keywords": "law school app, law students, bar takers, law school notes, law school outlines, case briefs, flashcards, study tools"
      });
      document.head.appendChild(appScript);

      // Add FAQ schema
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(getFAQSchema());
      document.head.appendChild(faqScript);
    };

    addStructuredData();
  }, []);

  // Add PushLap Growth affiliate widget for authenticated users on homepage
  useEffect(() => {
    if (user?.email) {
      // Create and inject the widget script immediately
      const script = document.createElement('script');
      script.src = 'https://www.pushlapgrowth.com/affiliate-widget.js';
      script.setAttribute('data-affiliate-program-id', '4a22edb4-77a0-4c3a-8f7e-5901641176e9');
      script.setAttribute('data-user-email', user.email);
      script.setAttribute('data-affiliate-widget', 'true');

      // Use requestAnimationFrame to ensure DOM is ready without artificial delay
      requestAnimationFrame(() => {
        const widgetContainer = document.getElementById('homepage-affiliate-widget');
        if (widgetContainer && !widgetContainer.querySelector('script[data-affiliate-widget]')) {
          widgetContainer.appendChild(script);
        }
      });

      // Cleanup on unmount
      return () => {
        const existingScript = document.querySelector('#homepage-affiliate-widget script[data-affiliate-widget="true"]');
        if (existingScript && existingScript.parentNode) {
          existingScript.parentNode.removeChild(existingScript);
        }
      };
    }
  }, [user?.email]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenImage) {
          setFullscreenImage(null);
        } else if (isModalOpen) {
          closeModal();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen, fullscreenImage]);

  // Global keyboard navigation for carousel
  useEffect(() => {
    const handleGlobalKeyPress = (e: KeyboardEvent) => {
      // Only handle if no input/textarea is focused and modal is not open
      if (isModalOpen || document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyPress);
    return () => document.removeEventListener('keydown', handleGlobalKeyPress);
  }, [isModalOpen]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % chatDemos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + chatDemos.length) % chatDemos.length);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <PageLayout>
      <div
        className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950"
        style={{
          minHeight: '100vh',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Hero Section */}
        <section id="top" className="pt-16 pb-16 md:pt-20 md:pb-24 relative overflow-hidden">
          <div className="absolute inset-0 animated-gradient opacity-60"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#F37022]/10 rounded-full filter blur-3xl animate-float-slow"></div>
            <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#00178E]/10 rounded-full filter blur-3xl animate-float-medium"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 box-border relative z-10">

            {/* Hero Content */}
            <div className="text-center mt-16">
              <h1 className="text-3xl md:text-5xl font-bold text-black dark:text-white mb-8">
                Your companion for mastering law school and the bar exam.
              </h1>

              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
                JD Simplified makes sense of the chaos so you can focus on mastering the law — not losing sleep over it.
              </p>

              <div className="flex flex-col md:flex-row gap-4 mt-8 justify-center items-center">
                {user ? (
                  <TrackingLink
                    href="/dashboard"
                    campaign="homepage_hero"
                    content="open_dashboard"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Open your dashboard
                  </TrackingLink>
                ) : (
                  <TrackingLink
                    href="/auth?tab=signup"
                    campaign="homepage_hero"
                    content="start_free"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Rocket className="w-5 h-5" />
                    Start free today
                  </TrackingLink>
                )}

                {/* Quiet secondary CTA — widens the promise toward the bar without
                    bar-prep-first-ing the homepage. UTM matches brief exactly:
                    utm_source=homepage&utm_medium=web&utm_campaign=homepage_hero&utm_content=nextgen_secondary */}
                <TrackingLink
                  href="/nextgen-prep"
                  campaign="homepage_hero"
                  content="nextgen_secondary"
                  utmParams={{ utm_source: 'homepage', utm_medium: 'web' }}
                  className="inline-flex items-center justify-center gap-1 text-[#F37022] hover:text-[#E35D10] font-medium py-3 px-2 transition-colors"
                >
                  See bar prep
                  <ArrowRight className="w-4 h-4" />
                </TrackingLink>
              </div>
            </div>
          </div>
        </section>

        {/* Problem / Promise Section */}
        <section id="problem" className="py-20 bg-white dark:bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 box-border text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-6">
              Law school makes it complicated. <span className="whitespace-nowrap">We make it clear.</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Outlines here, notes there, case briefs everywhere. JD Simplified puts it all in one connected platform—so you can stop surviving law school and start mastering it.
            </p>
            {/* Smooth-scroll to the Practice group. react-router <Link to="#practice">
                only mutates location.hash, which (a) doesn't fire the navbar's
                hash-scroll effect on a re-click when the hash is unchanged, and
                (b) depends on AskJDSNavbar being mounted. An explicit onClick
                scrollIntoView mirrors the navbar's own working anchor pattern and
                is self-contained. We still update the URL hash so the link is
                deep-link/shareable. */}
            <a
              href="#practice"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('practice')?.scrollIntoView({ behavior: 'smooth' });
                if (window.history.replaceState) {
                  window.history.replaceState(null, '', '#practice');
                }
              }}
              className="inline-flex items-center gap-2 text-[#F37022] hover:text-[#E35D10] font-medium text-lg transition-colors"
            >
              See how it works
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>

        {/* ============================================================
            FEATURE MIDDLE — grouped, nav-mirrored IA (2026-06).
            Replaces the prior flat feature run. Order:
              Practice (blue) → Library (neutral) → Notes (blue) →
              Ask JDS (neutral) → Community (blue).
            Background alternation continues from Problem (white) above
            and into NonServices below. The hinge "Stop guessing what to
            study." appears exactly once, in the Practice group headline.
            ============================================================ */}

        {/* GROUP: Practice — carries the page hinge. bg blue.
            Anchor id="practice" (Problem "see how it works" links here). */}
        <FeatureGroup
          id="practice"
          eyebrow="Practice"
          headline="Stop guessing what to study."
          subline="Practice-first study that builds real exam instincts."
          bg="blue"
        >
          {/* Study Mode — visual = interactive flashcard demo (image LEFT).
              CTA → /flashcards/study (Study Mode itself, NOT /flashcards
              collections), per founder. Body kept accurate: no "more cards
              added as you study" (verified fiction). Tagline restored. */}
          <CompactFeatureRow
            heading="Drill it until it sticks."
            body="Drill black-letter law, test your recall, and track what you've mastered. Each session builds on the last — and the topics worth your focus start to surface."
            tagline="Review smarter. Remember longer."
            side="left"
            cta={{ text: 'Enter Study Mode', href: '/flashcards/study', content: 'enter_study_mode' }}
            visual={
              <Suspense fallback={<SectionPlaceholder minHeight="420px" />}>
                <HomepageFlashcardDemo />
              </Suspense>
            }
          />

          {/* Issue Spotter — screenshot RIGHT. Original copy restored. */}
          <CompactFeatureRow
            heading="What's the issue?"
            body="Practice spotting legal issues before exam day. Upload a fact pattern, identify what you see, and get clear feedback on what you caught — and what you missed."
            tagline="No pressure. Just practice that builds real exam instincts."
            side="right"
            image="JDS-Issue-Spotter"
            imageVersion="&v=20260615"
            imageAlt="Issue Spotter interface showing fact pattern analysis with identified legal issues"
            cta={{ text: 'Try Issue Spotter', href: '/issue-spotter', content: 'try_issue_spotter' }}
            onImageZoom={setFullscreenImage}
          />

          {/* Essay Coach — screenshot LEFT. Original copy restored. */}
          <CompactFeatureRow
            heading="Feedback on your writing — the kind that helps."
            body="See exactly where points were earned or lost — issue coverage, rule accuracy, analysis depth. One clear breakdown. No guesswork."
            tagline="So you know what to fix next time — and why."
            side="left"
            image="JDS-Essay-Coach"
            imageVersion="&v=20260615"
            imageAlt="Essay Coach interface showing detailed feedback on legal writing with issue coverage, rule accuracy, and analysis depth"
            cta={{ text: 'Try Essay Coach', href: '/essay-coach', content: 'try_essay_coach' }}
            onImageZoom={setFullscreenImage}
          />
        </FeatureGroup>

        {/* GROUP: Library — the prepared material. bg neutral.
            Anchor id="library". Retains the Tacila testimonial verbatim. */}
        <FeatureGroup
          id="library"
          eyebrow="Library"
          headline="The groundwork, already laid."
          subline="Case briefs, outlines, and courses — the foundation underneath every practice session."
          bg="neutral"
        >
          {/* Case Briefs — screenshot RIGHT. id="caselaw" preserved
              (AskJDSNavbar + NotFoundPage link to /#caselaw). */}
          <CompactFeatureRow
            id="caselaw"
            heading="Case briefs that actually brief you."
            body="Clear, structured case summaries that highlight what matters — and why it matters on exams. Facts, rules, reasoning, and takeaways. Nothing extra. Everything essential."
            tagline="The cases you need. The clarity you deserve."
            side="right"
            image="JDS-Case-Law"
            imageVersion="&v=20260615"
            imageAlt="Case brief interface showing facts, rules, reasoning, and takeaways for a law school case"
            cta={{ text: 'Browse case briefs', href: '/cases', content: 'explore_cases' }}
            onImageZoom={setFullscreenImage}
          />

          {/* Outlines — screenshot LEFT. Tagline + CTA restored. */}
          <CompactFeatureRow
            heading="Outlines you can make your own."
            body="Start from full subject outlines — full, cram, and bar-focused versions — copy them into your notes with one click, or upload your own and get clear feedback on what's missing."
            tagline="Start with structure. End with clarity."
            side="left"
            image="JDS-Outlines"
            imageVersion="&v=20260615"
            imageAlt="Dynamic law school outline view with topic hierarchy and drag-and-drop sections"
            cta={{ text: 'Browse outlines', href: '/features/outlines', content: 'browse_outlines' }}
            onImageZoom={setFullscreenImage}
          />

          {/* Testimonial — Outlines (retained verbatim). Placed immediately
              after the Outlines row (and before Courses) so it clearly endorses
              the outlines-feedback experience the quote describes, not Courses. */}
          <div className="max-w-2xl mx-auto mt-10 md:mt-12">
            <Testimonial
              variant="compact"
              quote="Uploading my outlines and getting feedback made a huge difference in my preparation."
              name="Tacila F. De Lima"
              credentials={"LL.M., Ohio State Moritz ’17"}
              photoSrc="/images/testimonials/tacila.jpg"
              photoAlt="Tacila F. De Lima"
              trustpilotUrl={TRUSTPILOT_URL}
            />
          </div>

          {/* Courses — slim, centered, NO screenshot */}
          <div className="py-8 md:py-10 text-center max-w-2xl mx-auto">
            <h3 className="text-xl md:text-2xl font-bold text-black dark:text-white mb-3">
              Courses built for how law students learn.
            </h3>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Structured lessons in Civil Procedure, Intestacy, Legal Writing, and more — built to deepen what you practice in Study Mode.
            </p>
            <TrackingLink
              href="/courses"
              campaign="homepage_features"
              content="view_courses"
              className="inline-flex items-center gap-1.5 mt-4 text-[#F37022] hover:text-[#E35D10] font-medium transition-colors"
            >
              View courses
              <ArrowRight className="w-4 h-4" />
            </TrackingLink>
          </div>
        </FeatureGroup>

        {/* BEAT: Notes — its own top-level beat (a workspace, not reference
            content, so NOT folded into Library). bg blue. Anchor id="notes". */}
        <FeatureGroup
          id="notes"
          eyebrow="Notes"
          headline="A workspace that keeps up with you."
          bg="blue"
        >
          <CompactFeatureRow
            body="Take quick, focused notes with auto-save, instant search, and legal-specific templates — case briefs, class notes, study outlines — organized your way, on any device."
            tagline="Your notes. Your way. Always saved."
            side="right"
            image="JDS-Notes"
            imageVersion="&v=20260615"
            imageAlt="JD Simplified note editor with tags, highlighted case links, and connected outline references"
            cta={{ text: 'Start taking notes', href: '/notes', content: 'start_notes' }}
            onImageZoom={setFullscreenImage}
          />
        </FeatureGroup>

        {/* BEAT: Ask JDS — its own beat. bg neutral. Anchor id="chat"
            (preserved). Retains the Vincent testimonial verbatim. */}
        <FeatureGroup
          id="chat"
          eyebrow="Ask JDS"
          headline="When the law stops making sense, just ask."
          bg="neutral"
        >
          <CompactFeatureRow
            body="Your built-in companion breaks down complex doctrines, translates professor-speak into plain English, and helps you spot issues before the next cold call."
            tagline="The more you use the platform, the better Ask JDS gets at surfacing your weak spots."
            side="left"
            image="JDS-Chat"
            imageVersion="&v=20260615"
            imageAlt="JD Simplified chat explaining a law doctrine in structured, easy-to-read format"
            cta={{ text: 'Start a chat', href: '/chat', content: 'start_chat' }}
            onImageZoom={setFullscreenImage}
          />

          {/* Testimonial — Chat (retained verbatim) */}
          <div className="max-w-2xl mx-auto mt-10 md:mt-12">
            <Testimonial
              variant="compact"
              quote="JD Simplified gave me actual structure. Ask JDS was like having a tutor on call at 2am when I was stuck on a rule."
              name="Vincent"
              photoSrc="/images/testimonials/vincent.jpeg"
              photoAlt="Vincent"
              trustpilotUrl={TRUSTPILOT_URL}
            />
          </div>
        </FeatureGroup>

        {/* GROUP: Community — COMING SOON (founder-approved). bg blue.
            Single compact band, NO screenshot, with a visible "Coming soon"
            pill. Anchor id="community". */}
        <FeatureGroup
          id="community"
          eyebrow="Community"
          headline="You're not studying alone."
          subline="Discussions and study groups, built for law students — free, and coming soon."
          bg="blue"
        >
          <div className="py-8 md:py-10 text-center max-w-2xl mx-auto">
            <span className="inline-block bg-[#F37022]/10 text-[#F37022] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
              Coming soon
            </span>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              A free space to compare notes, work through fact patterns together, and ask the questions you'd rather not raise in class. Launching soon for every JD Simplified account.
            </p>
          </div>
        </FeatureGroup>

        <Suspense fallback={<SectionPlaceholder minHeight="280px" />}>
          <NonServicesSection />
        </Suspense>

        <Suspense fallback={<SectionPlaceholder minHeight="240px" />}>
          <AboutSection />
        </Suspense>

        {/* Carry Forward + Journey — consolidated (2026-06).
            This single section replaces the former "Master law school. Master
            yourself." CTA heading/subcopy with the carry-forward message, and
            nests the 1L→bar journey three-beat directly beneath it. The prior
            standalone "Carry Forward" section and standalone "Journey/Ecosystem"
            strip were merged here to remove duplication — one continuity beat
            right before the final CTA button.
            HARD GUARDRAIL: knowledge & progress carry; readiness scores do NOT.
            "Knowledge and progress carry over" is the ceiling claim.
            CTA button (Start Chatting / Sign Up) from the old "Master law
            school" section is PRESERVED below the journey cards.
            Pass 1 reorder (2026-06): moved to IMMEDIATELY BEFORE Pricing so the
            carry-forward value crescendo leads into the pricing ask. */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800 relative overflow-hidden">
          <SectionDivider id="carry-forward" label="Carry Forward" className="mb-20" />
          <div className="max-w-3xl mx-auto px-4 text-center relative z-10 box-border">
            <div className="w-16 h-16 bg-[#F37022]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Layers className="w-8 h-8 text-[#F37022]" />
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-6">
              Nothing you learn gets thrown away.
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              What you master in law school — subject by subject — stays with you. When you move to bar prep, NextGen Prep builds on the work you've already done. Your knowledge and progress carry over, so you start ahead, not from scratch.
            </p>
          </div>

          {/* Journey / Ecosystem three-beat — 1L→bar arc, nested in this section */}
          <div className="max-w-5xl mx-auto px-4 mt-16 box-border relative z-10">
            <div className="grid md:grid-cols-3 gap-8 md:gap-6 items-stretch">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md border border-gray-200 dark:border-gray-700">
                <span className="inline-block bg-[#F37022]/10 text-[#F37022] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                  In law school
                </span>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Understand the law, build your record.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md border border-gray-200 dark:border-gray-700">
                <span className="inline-block bg-[#F37022]/10 text-[#F37022] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                  For the bar
                </span>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Practice-first prep that knows where you already stand.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md border border-gray-200 dark:border-gray-700">
                <span className="inline-block bg-[#00178E]/10 text-[#00178E] dark:bg-blue-400/10 dark:text-blue-400 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                  One account
                </span>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  From your first case brief to bar exam day.
                </p>
              </div>
            </div>
          </div>

          {/* CTA dedupe (Pass 2): the old filled "Start Chatting / Sign Up"
              button was REMOVED so the page has a single hard close (Payoff).
              Replaced with a soft text link. Logged-out → /nextgen-prep
              (utm_content=carryforward_nextgen); logged-in → /chat. */}
          <div className="max-w-4xl mx-auto px-4 text-center mt-16 relative z-10 box-border">
            <div className="flex justify-center">
              {user ? (
                <TrackingLink
                  href="/dashboard"
                  campaign="homepage_conversion"
                  content="carryforward_resume"
                  className="inline-flex items-center gap-2 text-[#F37022] hover:text-[#E35D10] font-medium text-lg transition-colors"
                >
                  Pick up where you left off
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              ) : (
                <TrackingLink
                  href="/nextgen-prep"
                  campaign="homepage_conversion"
                  content="carryforward_nextgen"
                  className="inline-flex items-center gap-2 text-[#F37022] hover:text-[#E35D10] font-medium text-lg transition-colors"
                >
                  See how it carries forward
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800 overflow-x-hidden">
          <SectionDivider id="pricing" label="Pricing" className="mb-20" />
          <div className="max-w-4xl mx-auto text-center px-4 box-border">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-8">Simple, transparent pricing</h2>
          </div>
          <div className="px-4 box-border">
            <Suspense fallback={<SectionPlaceholder minHeight="600px" />}>
              <HomepagePricingSection />
            </Suspense>
          </div>

          {/* Peer-proof strip — "Where our students come from" (founder-approved
              2026-06-29, board sWFYffbpsry6E6zZFw4e). INTEGRATED (2026-06-29) into
              the BOTTOM of the Pricing section — it is no longer its own <section>
              with its own background/card. It renders inside the Pricing container,
              on the SAME pricing background (gray-50 / dark:gray-800), after the
              pricing cards and the "No commitments…" line, as the closing row of the
              pricing block. The previous treatment (separate white section + a
              floating max-w-2xl boxed card + orange accent banner) made it read as an
              orphaned white island; that island/card framing is removed. What stays:
              a subtle top divider for separation (NOT a boxed card), the strengthened
              heading weight, and the names present. Container matches the pricing
              header width (max-w-4xl). Still deliberately understated: real text (NOT
              logos/crests/colors), STATIC (no marquee/auto-scroll), centered,
              flex-wrap on mobile. Framing is SIGNUP-only ("come from") — real
              signed-up .edu users — so NO usage verb ("study with"/"used by"/"trusted
              by"): Yale & Georgetown only signed up, so a usage claim beside them
              would be false. The "42+ law schools" scale phrase is an FTC-defensible
              aggregate. School list + wording + framing are founder-locked — only
              styling/placement changed. */}
          <div className="max-w-4xl mx-auto px-4 box-border mt-16 pt-10 border-t border-gray-200 dark:border-gray-700 text-center">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100">
              Where our students come from
            </h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
              From 42+ law schools
            </p>
            <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-base text-gray-700 dark:text-gray-200">
              {[
                'Yale',
                'Georgetown',
                'UC Berkeley',
                'UCLA',
                'USC',
                'Boston University',
                'Iowa',
                'Pepperdine',
              ].map((school, i) => (
                <li key={school} className="flex items-center gap-x-3">
                  {i > 0 && (
                    <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                  )}
                  <span>{school}</span>
                </li>
              ))}
            </ul>
            {/* Non-affiliation disclaimer — closes the implied-endorsement soft spot
                (nominative fair use is otherwise clean: text-only names, signup framing,
                no logos). Quiet footnote treatment, stays muted/small. */}
            <p className="mt-6 text-[0.6875rem] leading-relaxed text-gray-400 dark:text-gray-500">
              JD Simplified is not affiliated with or endorsed by these institutions.
            </p>
          </div>
        </section>

        {/* FAQ Section — founder-approved (2026-06). RELOCATED (2026-06-29) to sit
            AFTER Pricing (was before). Rendered as native <details>/<summary>
            accordion blocks — accessible, JS-free, keyboard-operable. NO second
            FAQPage JSON-LD here: the page already injects getFAQSchema() (kept in
            sync with these 7 Q&As in src/seo/schema-helpers.ts).
            BG alternation (tail): Pricing(gray-50) → FAQ(white) → Payoff(white). */}
        <section id="faq" className="py-20 bg-white dark:bg-gray-950 overflow-x-hidden" style={{ scrollMarginTop: '6rem' }}>
          <SectionDivider id="faq" label="FAQ" className="mb-20" />
          <div className="max-w-4xl mx-auto text-center px-4 box-border mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white">
              Frequently asked questions
            </h2>
          </div>
          <div className="max-w-3xl mx-auto px-4 box-border space-y-4">
            {[
              {
                q: 'What is JD Simplified?',
                a: 'JD Simplified is a practice-first learning system for law students and bar takers. It brings your case briefs, outlines, notes, flashcards, and Ask JDS into one connected place — so you can understand the law, practice applying it, and focus on what actually matters.',
              },
              {
                q: 'Is it really free?',
                a: isNative
                  ? 'Yes. You can study, take notes, build outlines, and use core tools on the free plan, with no time limit. The Unlimited plan adds everything — all of Study Mode, courses, Issue Spotter, Essay Coach, and Ask JDS. Plans are managed from your account on the web.'
                  : 'Yes. You can study, take notes, build outlines, and use core tools on the free plan, with no time limit. Unlimited ($20/month or $200/year) adds everything — all of Study Mode, courses, Issue Spotter, Essay Coach, and Ask JDS.',
              },
              {
                q: 'Do I need a credit card to start?',
                a: 'No. You can sign up and start studying without a card. You only enter payment details if you choose to upgrade to Unlimited.',
              },
              {
                q: 'How much does JD Simplified cost?',
                a: isNative
                  ? "There's a free plan, and an Unlimited plan that includes all study tools, courses, flashcards, and Ask JDS. Plans are viewed and managed from your account on the web."
                  : "There's a free plan, and an Unlimited plan at $20/month or $200/year that includes all study tools, courses, flashcards, and Ask JDS.",
              },
              {
                q: 'Can JD Simplified help with the bar exam?',
                a: "Yes. The same skills you build in law school — issue-spotting, applying rules, writing clear analysis — are what the bar tests. When you're ready, NextGen Prep is our practice-first prep built around how the NextGen bar exam actually works.",
              },
              {
                q: "What's the difference between JD Simplified and NextGen Prep?",
                a: 'JD Simplified is your day-to-day system for law school. NextGen Prep is practice-first bar prep for the NextGen UBE, built in collaboration with Studicata. One account carries you from your first case brief through bar exam day.',
              },
              {
                q: 'Will my work carry over when I start bar prep?',
                a: "Your knowledge and progress carry over — the studying you do in JD Simplified builds on into NextGen Prep, so you start ahead instead of from scratch. (Your bar-readiness is measured fresh inside NextGen Prep's own practice.)",
              },
            ].map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 list-none text-base md:text-lg font-semibold text-black dark:text-white">
                  <span>{item.q}</span>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 text-[#F37022] transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Payoff / Transformation Section — emotional closer (final content
            section). 2026-06-29 reorder: FAQ moved AFTER Pricing and the
            peer-proof schools strip was integrated into the bottom of Pricing,
            so the tail is now Pricing(gray-50, w/ integrated schools strip) →
            FAQ(white) → Payoff(white). FAQ's SectionDivider + the divider above
            this closer keep the two white blocks visually distinct. */}
        <section id="payoff" className="py-20 bg-white dark:bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 box-border text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-6">
              From panic to perspective. <span className="whitespace-nowrap">From outlines to outcomes.</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              JD Simplified is a complete system that helps you think like a lawyer while staying sane like a human. Understand more, retain more, and finally feel ahead of your syllabus instead of buried under it.
            </p>
            <TrackingLink
              href="/auth?tab=signup"
              campaign="homepage_conversion"
              content="payoff_start_free"
              className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              Start free today
              <ArrowRight className="w-5 h-5" />
            </TrackingLink>
          </div>
        </section>


      </div>

      {/* Image Enlargement Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative w-full h-full max-w-[90vw] max-h-[99vh] flex items-center justify-center">
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 bg-black/70 text-white p-3 rounded-full hover:bg-black/90 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              data-twic-src={`media:${chatDemos[currentImageIndex].image.substring(1)}`}
              data-twic-transform="quality=95/output=auto"
              data-twic-eager
              alt={chatDemos[currentImageIndex].altText}
              className="max-w-full max-h-full object-contain rounded-lg screenshot-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Fullscreen Screenshot Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative w-full h-full max-w-[95vw] max-h-[95vh] flex items-center justify-center">
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm z-10"
              aria-label="Close fullscreen"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={fullscreenImage.startsWith('media:') ? undefined : fullscreenImage}
              data-twic-src={fullscreenImage.startsWith('media:') ? fullscreenImage : undefined}
              data-twic-transform={fullscreenImage.startsWith('media:') ? "quality=95/output=auto" : undefined}
              data-twic-eager={fullscreenImage.startsWith('media:') ? true : undefined}
              alt="Screenshot fullscreen view"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            />
          </div>
        </div>
      )}

    </PageLayout>
  );
}